import { useMemo, useState } from "react";
import moment from "moment/moment";
import { useFormikContext } from "formik";
import { IconFileBroken, IconX } from "@tabler/icons-react";

// @ts-expect-error pure js
import useAuthenticatedAxios from "../../../hooks/use-authenticated-axios";
// @ts-expect-error pure js
import useFiles from "../../../hooks/use-files";
// @ts-expect-error pure js
import useLeaseTemplates from "../../../hooks/use-lease-templates";
// @ts-expect-error pure js
import useProperties from "../../../hooks/use-properties";
// @ts-expect-error pure js
import usePropertyFolders from "../../../hooks/use-property-folders";
import { usePropertyFolderDiagnostics } from "../../../hooks/use-property-diagnostics";
import { fileCoversDiagnostic } from "../../../utils/documentStatus";
import type { DocumentFile } from "../../../models/document";
import type { PropertyFolder } from "../../../models/propertyFolder";
import { TenantLeaseType } from "../../../utils/constants/tenants";
import {
  ACCEPTED_FILE_EXTENSIONS,
  DIAGNOSTIC_TAGS,
  // @ts-expect-error pure js
} from "../../../utils/constants/documents";
import {
  type DiagnosticRuleInput,
  getDiagnosticRequirement,
} from "../../../utils/diagnosticRules";
import type { Property } from "../../../models/property";
// @ts-expect-error pure js
import { formatFileSize } from "../../../api/Functions";
// @ts-expect-error pure js
import { handleAPIError } from "../../../utils";
// @ts-expect-error pure js
import { canPreprocessImage, preprocessImage } from "../../../utils/documents";
// @ts-expect-error pure js
import { validateUploadedFile } from "../../../utils/documents";

import Accordion, { AccordionItem } from "../../UI/Accordion";
import Callout from "../../UI/Callout";
import Card from "../../UI/Card";
import DocumentDownloadLink from "../../UI/DocumentDownloadLink";
// @ts-expect-error pure js
import DocumentThumbnail from "../../UI/DocumentThumbnail";
import FileDropInput from "../../UI/FileDropInput";
import IconButton from "../../UI/IconButton";
import Status from "../../UI/Status";
import Text from "../../UI/Text";
import Title from "../../UI/Title";
// @ts-expect-error pure js
import ToggleSwitchFormik from "../../UI/ToggleSwitchFormik";
import { useUploadLock } from "../../UI/useUploadLock";
import UploadProgress from "../../UI/UploadProgress";

import styles from "./TenantAddFormDocument.module.css";
import CountBadge from "../../UI/CountBadge.tsx";

interface AnnexeDoc {
  field: string;
  name: string;
  annexeName: string;
  obligatoire?: boolean;
}

const ANNEXES_HABITATION: AnnexeDoc[] = [
  {
    field: "join_notice",
    name: "Notice d'information du locataire",
    annexeName: "notice",
    obligatoire: true,
  },
  {
    field: "join_guide_reparations",
    name: "Guide des réparations locatives",
    annexeName: "guide-reparations",
  },
];

interface AnnexeRowProps {
  doc: AnnexeDoc;
}

function AnnexeRow({ doc }: AnnexeRowProps) {
  const downloadUrl = `lease-annexes/${doc.annexeName}`;
  return (
    <Card padding="xs" className={styles.fileRow}>
      <DocumentDownloadLink
        downloadUrl={downloadUrl}
        mimetype="application/pdf"
        className={styles.docThumb}
      >
        <DocumentThumbnail
          downloadUrl={downloadUrl}
          mimetype="application/pdf"
        />
      </DocumentDownloadLink>
      <div className={styles.fileInfo}>
        <div>
          <Text weight="bold" as="span">
            {doc.name}
          </Text>{" "}
          {doc.obligatoire && (
            <Text variant="deci" color="muted" as="em">
              (obligatoire)
            </Text>
          )}
        </div>
        <Text variant="deci" color="muted">
          PDF
        </Text>
      </div>
      <ToggleSwitchFormik name={doc.field} />
    </Card>
  );
}

interface RequiredDoc {
  tag: string;
  name: string;
  needsValidity?: boolean;
}

const OTHER_DOC: RequiredDoc = { tag: "autre", name: "Autre" };

/** Libellés des diagnostics du dossier de diagnostic technique, dans l'ordre de `DIAGNOSTIC_TAGS`. */
const DIAGNOSTIC_DOCS: Record<string, Omit<RequiredDoc, "tag">> = {
  DPE: { name: "Diagnostic de performance énergétique", needsValidity: true },
  plomb: {
    name: "Constat de risque d'exposition au plomb",
    needsValidity: true,
  },
  amiante: {
    name: "État mentionnant la présence ou l'absence d'amiante",
    needsValidity: true,
  },
  elec: { name: "Diagnostic électricité", needsValidity: true },
  gaz: { name: "Diagnostic gaz", needsValidity: true },
  risque_naturel: {
    name: "État des risques et pollutions (ERP)",
    needsValidity: true,
  },
  bruit: { name: "État des nuisances sonores aériennes" },
  annexe_environnementale: { name: "Annexe environnementale" },
};

const AUTRE_LOCAL_LEASE_TYPES: string[] = [
  TenantLeaseType.GARAGE,
  TenantLeaseType.PARKING,
  TenantLeaseType.STOCKAGE,
];

/**
 * Diagnostics exigibles pour ce bail : ceux que les caractéristiques du bien
 * rendent obligatoires, hors dispenses « non concerné » posées sur le bien.
 * Tant que le bien n'est pas chargé, seul le type de bail est connu et tout ce
 * qui peut l'être est demandé.
 */
function getRequiredDocs(
  property: Property | undefined,
  lease: LeaseTemplate,
): RequiredDoc[] {
  const fallback: DiagnosticRuleInput = {
    type: TenantLeaseType.isCommercial(lease.type)
      ? "commercial"
      : lease.type !== undefined && AUTRE_LOCAL_LEASE_TYPES.includes(lease.type)
      ? "garage"
      : "habitation",
  };
  const input: DiagnosticRuleInput = property ?? fallback;
  const notApplicable = property?.not_applicable_diagnostics ?? [];
  return (DIAGNOSTIC_TAGS as string[])
    .filter(
      (tag) =>
        !notApplicable.includes(tag) &&
        getDiagnosticRequirement(input, tag).required,
    )
    .map((tag) => ({ tag, ...DIAGNOSTIC_DOCS[tag] }));
}

interface FileEntry {
  id: number;
  tag: string;
  filename?: string;
  size?: number;
  shared?: boolean;
  valid_until?: string;
  src?: string;
  mimetype?: string;
  meta?: DocumentFile["meta"];
  /** Document déposé sur l'immeuble du bien, qui en hérite : non supprimable d'ici. */
  inherited?: boolean;
}

/** Diagnostics déposés sur l'immeuble du bien et ses dossiers parents, du plus proche au plus lointain. */
function inheritedDiagnostics(
  property: Property | undefined,
  foldersById: Record<number, PropertyFolder | undefined>,
  diagnosticsByFolder: Record<number, DocumentFile[] | undefined>,
): FileEntry[] {
  const entries: FileEntry[] = [];
  let folderId = property?.id_property_folder;
  while (folderId !== undefined && folderId !== null) {
    const folder = foldersById[folderId];
    (diagnosticsByFolder[folderId] ?? []).forEach((file) =>
      entries.push({ ...file, inherited: true }),
    );
    folderId = folder?.id_parent;
  }
  return entries;
}

type DocStatus = "valid" | "expired" | "missing";

function computeStatus(files: FileEntry[], needsValidity?: boolean): DocStatus {
  if (files.length === 0) return "missing";
  if (!needsValidity) return "valid";
  const hasValid = files.some(
    (f) => !f.valid_until || moment(f.valid_until).isAfter(moment()),
  );
  return hasValid ? "valid" : "expired";
}

function StatusBadge({ status }: { status: DocStatus }) {
  if (status === "valid") return <Status color="success" text="Présent" />;
  if (status === "expired") return <Status color="neutral" text="Expiré" />;
  return <Status color="danger" text="Manquant" />;
}

interface DropZoneProps {
  tag: string;
  onUploaded: (file: FileEntry) => void;
}

function DropZone({ tag, onUploaded }: DropZoneProps) {
  const axios = useAuthenticatedAxios();
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const { acquire } = useUploadLock();

  async function handleFiles(files: File[]) {
    setError("");
    if (files.length === 0) {
      return;
    }
    const formData = new FormData();
    // Le formulaire reste verrouillé jusqu'à la réponse du serveur : c'est elle
    // qui rattache les fichiers au formulaire.
    const release = acquire();
    setUploading(true);
    setProgress(0);
    try {
      for (const file of files) {
        const validationError = validateUploadedFile(file);
        if (validationError) {
          setError(validationError);
          return;
        }
        let processed: File = file;
        if (canPreprocessImage(file)) {
          processed = await preprocessImage(file, false);
        }
        formData.append("selectedFiles", processed);
      }
      formData.append("tag", tag);
      const res = await axios.post("/files/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e: { loaded: number; total?: number }) => {
          setProgress(Math.round((e.loaded * 100) / (e.total || 1)));
        },
      });
      res.data.forEach((file: FileEntry) => onUploaded({ ...file, tag }));
    } catch (err) {
      setError(handleAPIError(err));
    } finally {
      setUploading(false);
      release();
    }
  }

  return (
    <div>
      <FileDropInput
        onFiles={handleFiles}
        accept={ACCEPTED_FILE_EXTENSIONS}
        text="Glissez-déposez le document."
        hasError={Boolean(error)}
      />
      <UploadProgress percent={progress} uploading={uploading} />
      <Callout variant="danger" className={styles.uploadError}>
        {error}
      </Callout>
    </div>
  );
}

interface RequiredDocRowProps {
  doc: RequiredDoc;
  files: FileEntry[];
  onUploaded: (file: FileEntry) => void;
  onRemove: (fileId: number) => void;
}

function RequiredDocRow({
  doc,
  files,
  onUploaded,
  onRemove,
}: RequiredDocRowProps) {
  const badge =
    doc.tag === "autre" ? (
      <CountBadge count={files.length} />
    ) : (
      <StatusBadge status={computeStatus(files, doc.needsValidity)} />
    );
  return (
    <AccordionItem value={doc.tag} title={doc.name} headerActions={badge}>
      <div className={styles.docBody}>
        {files.length > 0 && (
          <div className={styles.list}>
            {files.map((file) => (
              <UploadedDocRow
                key={file.id}
                file={file}
                onRemove={() => onRemove(file.id)}
              />
            ))}
          </div>
        )}
        <DropZone tag={doc.tag} onUploaded={onUploaded} />
      </div>
    </AccordionItem>
  );
}

interface UploadedDocRowProps {
  file: FileEntry;
  onRemove: () => void;
}

function UploadedDocRow({ file, onRemove }: UploadedDocRowProps) {
  return (
    <Card padding="xs" className={styles.fileRow}>
      <DocumentDownloadLink
        documentId={file.id}
        file={file}
        mimetype={file.mimetype}
        className={styles.docThumb}
      >
        <DocumentThumbnail
          documentId={file.id}
          file={file}
          mimetype={file.mimetype}
          fallback={<IconFileBroken />}
        />
      </DocumentDownloadLink>
      <div className={styles.fileInfo}>
        <Text weight="bold">{file.filename ?? file.src}</Text>
        {file.size !== undefined && (
          <Text variant="deci" color="muted">
            {formatFileSize(file.size)}
          </Text>
        )}
      </div>
      {file.inherited ? (
        <Status color="info" text="Hérité de l'immeuble" />
      ) : (
        <IconButton onClick={onRemove} label="Supprimer" icon={IconX} />
      )}
    </Card>
  );
}

interface TenantAddFormDocumentProps {
  isRegenerate?: boolean;
}

interface FormikValues {
  id_bien: string | number;
  id_lease_template: string | number;
  genBail?: string;
}

interface UseFilesReturn {
  files?: FileEntry[];
  addFiles: (files: Partial<FileEntry>[]) => Promise<unknown>;
  deleteFile: (fileId: number) => Promise<unknown>;
}

interface LeaseTemplate {
  type?: string;
  habitation?: boolean;
}

export default function TenantAddFormDocument({
  isRegenerate,
}: TenantAddFormDocumentProps) {
  const { values } = useFormikContext<FormikValues>();
  const propertyId = parseInt(String(values.id_bien));
  const {
    files = [],
    addFiles,
    deleteFile,
  } = useFiles({ propertyId }) as UseFilesReturn;
  const { leaseTemplatesById } = useLeaseTemplates({ withById: true }) as {
    leaseTemplatesById: Record<string | number, LeaseTemplate | undefined>;
  };
  const { propertiesById } = useProperties({ withById: true }) as {
    propertiesById: Record<number, Property | undefined>;
  };
  const property = propertiesById[propertyId];
  const { propertyFoldersById } = usePropertyFolders({ withById: true }) as {
    propertyFoldersById: Record<number, PropertyFolder | undefined>;
  };
  const { diagnosticsByFolder } = usePropertyFolderDiagnostics();
  const inheritedFiles = useMemo(
    () =>
      inheritedDiagnostics(property, propertyFoldersById, diagnosticsByFolder),
    [property, propertyFoldersById, diagnosticsByFolder],
  );

  const lease = useMemo<LeaseTemplate>(
    () => leaseTemplatesById[values.id_lease_template] || {},
    [leaseTemplatesById, values.id_lease_template],
  );

  const requiredDocs = useMemo<RequiredDoc[]>(
    () => getRequiredDocs(property, lease),
    [property, lease],
  );

  const sharedFiles = useMemo(
    () => (files ?? []).filter((f) => f.shared),
    [files],
  );

  async function handleUploaded(file: FileEntry) {
    try {
      await addFiles([
        {
          ...file,
          shared: true,
        },
      ]);
    } catch (e) {
      handleAPIError(e);
    }
  }

  async function handleRemove(fileId: number) {
    try {
      await deleteFile(fileId);
    } catch (err) {
      console.error(err);
    }
  }

  const docsWithOther = useMemo(
    () => [...requiredDocs, OTHER_DOC],
    [requiredDocs],
  );
  const requiredTags = useMemo(
    () => requiredDocs.map((d) => d.tag),
    [requiredDocs],
  );

  // Un fichier vaut pour un diagnostic s'il en porte le tag, un ancien tag ou s'il le
  // regroupe (PDF multi-diagnostics) ; un bien sans document propre hérite de son immeuble.
  const covers = (file: FileEntry, tag: string) =>
    fileCoversDiagnostic(file, tag);
  function filesForDoc(doc: RequiredDoc) {
    if (doc.tag === "autre") {
      return sharedFiles.filter(
        (f) => !requiredTags.some((tag) => covers(f, tag)),
      );
    }
    const own = sharedFiles.filter((f) => covers(f, doc.tag));
    return own.length > 0
      ? own
      : inheritedFiles.filter((f) => covers(f, doc.tag));
  }

  return (
    <div className={styles.root}>
      {isRegenerate && (
        <Callout variant="warning">
          Nous ne supprimerons pas l'ancien bail généré, vous devrez le
          supprimer depuis l'espace locataire, onglet "Documents".
        </Callout>
      )}

      {lease.habitation && (
        <>
          <Title as="h2" variant="deca" weight="bold">
            Documents annexes
          </Title>
          <div className={styles.list}>
            {ANNEXES_HABITATION.map((doc) => (
              <AnnexeRow key={doc.field} doc={doc} />
            ))}
          </div>
        </>
      )}

      <Title as="h2" variant="deca" weight="bold">
        Documents joints
      </Title>
      <Accordion className={styles.list} defaultValue={[OTHER_DOC.tag]}>
        {docsWithOther.map((doc) => (
          <RequiredDocRow
            key={doc.tag}
            doc={doc}
            files={filesForDoc(doc)}
            onUploaded={handleUploaded}
            onRemove={handleRemove}
          />
        ))}
      </Accordion>
    </div>
  );
}
